import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { AstrologerAnalysis, AstrologerProfile } from '../../../types/professional';

type AnalysisRow = AstrologerAnalysis & { astrologer_name?: string; client_name?: string };

export function AdminAstrologerAnalysesPage() {
  const [analyses, setAnalyses]   = useState<AnalysisRow[]>([]);
  const [loading, setLoading]     = useState(true);
  const [filterAp, setFilterAp]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [astrologers, setAstrologers]   = useState<Pick<AstrologerProfile, 'id' | 'professional_name'>[]>([]);

  useEffect(() => {
    Promise.all([
      supabase.from('astrologer_client_analyses').select('*').order('created_at', { ascending: false }).limit(500),
      supabase.from('astrologer_profiles').select('id, professional_name').order('professional_name'),
      supabase.from('astrologer_clients').select('id, name'),
    ]).then(([{ data: a }, { data: aps }, { data: c }]) => {
      const apMap: Record<string, string> = {};
      for (const ap of (aps ?? [])) apMap[ap.id] = ap.professional_name;
      const cMap: Record<string, string> = {};
      for (const cl of (c ?? [])) cMap[cl.id] = cl.name;
      setAstrologers((aps ?? []) as Pick<AstrologerProfile, 'id' | 'professional_name'>[]);
      setAnalyses(((a ?? []) as AstrologerAnalysis[]).map(an => ({
        ...an,
        astrologer_name: apMap[an.astrologer_id] ?? an.astrologer_id,
        client_name:     cMap[an.client_id] ?? 'Cliente',
      })));
      setLoading(false);
    });
  }, []);

  const filtered = analyses.filter(a => {
    const matchAp     = !filterAp     || a.astrologer_id === filterAp;
    const matchStatus = !filterStatus || a.status === filterStatus;
    return matchAp && matchStatus;
  });

  const totalTokens = filtered.reduce((s, a) => s + (a.tokens_used ?? 0), 0);
  const cardStyle: React.CSSProperties = { background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '1rem 1.25rem' };

  return (
    <div>
      <h1 style={{ fontSize: 'clamp(1.1rem,3vw,1.4rem)', color: 'var(--text-primary)', margin: '0 0 1.25rem', fontFamily: 'var(--font-display)' }}>
        Todas as Análises
      </h1>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {[
          { label: 'Total', value: filtered.length },
          { label: 'Concluídas', value: filtered.filter(a => a.status === 'completed').length },
          { label: 'Erros', value: filtered.filter(a => a.status === 'error').length },
          { label: 'Tokens usados', value: totalTokens.toLocaleString('pt-BR') },
        ].map(m => (
          <div key={m.label} style={{ ...cardStyle, flex: 1, minWidth: '100px' }}>
            <p style={{ margin: '0 0 0.2rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{m.label}</p>
            <p style={{ margin: 0, fontWeight: 700, color: 'var(--petrol)', fontFamily: 'var(--font-display)', fontSize: '1.25rem' }}>{loading ? '…' : m.value}</p>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <select className="form-input" style={{ flex: 1 }} value={filterAp} onChange={e => setFilterAp(e.target.value)}>
          <option value="">Todos os astrólogos</option>
          {astrologers.map(a => <option key={a.id} value={a.id}>{a.professional_name}</option>)}
        </select>
        <select className="form-input" style={{ width: 'auto' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Todos os status</option>
          <option value="completed">Concluída</option>
          <option value="processing">Processando</option>
          <option value="error">Erro</option>
          <option value="pending">Pendente</option>
        </select>
        <span style={{ alignSelf: 'center', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{filtered.length} análise{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-muted)', padding: '2rem' }}>Carregando…</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>Nenhum resultado.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {filtered.map(a => (
            <div key={a.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: '0 0 0.2rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>
                  {a.service_name}, {a.client_name}
                </p>
                <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                  {a.astrologer_name} · {new Date(a.created_at).toLocaleString('pt-BR')}
                  {a.tokens_used ? ` · ${a.tokens_used} tokens` : ''}
                  {a.generation_time_ms ? ` · ${(a.generation_time_ms / 1000).toFixed(1)}s` : ''}
                </p>
              </div>
              <span style={{ fontSize: 'var(--text-caption)', color: a.status === 'completed' ? '#4ade80' : a.status === 'error' ? '#f87171' : '#fbbf24', flexShrink: 0 }}>
                {a.status === 'completed' ? '✅' : a.status === 'error' ? '❌' : '⏳'} {a.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
